import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  AppBar,
  Typography,
  Button,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Avatar,
  Chip,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  LinearProgress,
  TextField,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import AppDialog from "../../components/common/AppDialog.tsx";
import sampleImage from "../../assets/images/inventory.png";
import { useSelector } from "react-redux";
import { externalpostApi, externalgetApi } from "../../services/externalApis.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import { validateImageSpecs } from "../../utils/common/ImageValidation.tsx";
import axios from "axios";
import { ENDPOINT } from "../../api/EndPoints.tsx";

// Import your API endpoints
import { ITEM_API_01, ITEM_CREATE_ITEMS_API, UPLOAD_API } from "../../api/ApiModel.tsx";

// Types and Interfaces
interface UserParams {
  COMPCODE?: string;
  STAFFMAPLOCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface RootState {
  user: UserState;
}

interface FormData {
  itemName?: string;
  description?: string;
  modelName?: string;
  unitsPerPackage?: string | number;
  mainCategory?: string | number;
  category?: string | number;
  subCategory?: string | number;
  genericName?: string | number;
  packageType?: string | number;
  uom?: string | number;
  brand?: string | number;
  [key: string]: any;
}

interface Variant {
  type: string;
  values: string[];
}

interface VariantCombination {
  [key: string]: string;
}

interface LocationState {
  formData?: FormData;
  variants?: Variant[];
  variantCombinations?: VariantCombination[];
  combinationCount?: number;
  commonImages?: any[];
  imageType?: string;
  itemId?: string | number;
  isEditMode?: boolean;
  selectedItem?: any;
  savedItemData?: any;
  fromCreateVariant?: boolean;
  updatedItemDescriptions?: Array<{ id: string | number; description: string }>;
}

interface ItemRow {
  id: string | number;
  itemcode: string | number;
  itemname: string;
  variant: string;
  description: string;
  createddate?: string;
  uploadtype?: string;
  variantData?: VariantCombination;
  variantKey?: string;
  variantType?: string;
  variantValues?: string[];
  isSellingItem?: boolean;
  isInventoryItem?: boolean;
}

interface ImageData {
  id: string | number;
  data?: string;
  name: string;
  size: number;
  type: string;
  filename: string;
  filepath?: string;
  filepathUrl?: string;
  variantIdentifier?: string;
  isDefault: boolean;
  isExisting?: boolean;
  base64Data?: string;
  doctypecode?: string;
  doctypeothername?: string;
  remark?: string;
  rawData?: any;
  sourceItem?: number;
  [key: string]: any;
}

interface ApiResponseItem {
  itemcode?: string | number;
  itemname?: string;
  variantdata?: string;
  productimgs?: string;
  docpathjson?: string;
  maincatcode?: string | number;
  catcode?: string | number;
  subcatcode?: string | number;
  genericcode?: string | number;
  packagecode?: string | number;
  measuringunitcode?: string | number;
  uomcode?: string | number;
  packagingcode?: string | number;
  packageunits?: number;
  descrip?: string;
  isselling?: string | number | boolean;
  isinv?: string | number | boolean;
  modelname?: string;
  brandcode?: string | number;
  [key: string]: any;
}

interface UploadResponse {
  filepath?: string;
  [key: string]: any;
}

// Debounce hook
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Helper to parse isselling/isinv values from API (handles "Y"/"N", empty string, boolean, number)
const parseYNBool = (raw: any, fallback: boolean): boolean => {
  if (raw === undefined || raw === null) return fallback;
  const str = String(raw).trim().toUpperCase();
  if (str === "Y" || str === "YES" || str === "TRUE" || str === "1") return true;
  if (str === "N" || str === "NO" || str === "FALSE" || str === "0") return false;
  return fallback;
};

// Helper: enforce "at least one item type must be true" invariant.
// If both are false, default to Selling = true, Inventory = false.
const enforceItemType = (
  selling: boolean,
  inventory: boolean
): { isSellingItem: boolean; isInventoryItem: boolean } => {
  if (!selling && !inventory) {
    return { isSellingItem: true, isInventoryItem: false };
  }
  return { isSellingItem: selling, isInventoryItem: inventory };
};

function ItemListView(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme) as any;
  const navigate = useNavigate();
  const location = useLocation();

  // Get user from Redux store
  const SYS_USER = useSelector((state: RootState) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE, USERNAME } = SYS_USER.USER_PARAM || {};

  // API states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "warning" | "info">("success");
  const [alertMessage, setAlertMessage] = useState<string>("");

  // Get data from navigation state
  const locationState = (location.state as LocationState) || {};
  const {
    formData,
    variants,
    variantCombinations,
    combinationCount,
    commonImages = [],
    imageType = "common",
    itemId,
    isEditMode = false,
    selectedItem,
    savedItemData,
    fromCreateVariant = false,
    updatedItemDescriptions: initialUpdatedDescriptions = []
  } = locationState;

  const flagSource = selectedItem ?? savedItemData;
  const rawSeedSelling = (flagSource?.isselling ?? flagSource?.ISSELLING ?? "Y") !== "N";
  const rawSeedInventory = (flagSource?.isinv ?? flagSource?.ISINV ?? "N") === "Y";
  const { isSellingItem: seedSelling, isInventoryItem: seedInventory } =
    enforceItemType(rawSeedSelling, rawSeedInventory);

  // Generate item combinations from variants
  const [items, setItems] = useState<ItemRow[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filteredItems, setFilteredItems] = useState<ItemRow[]>([]);
  const [editingDescriptionId, setEditingDescriptionId] = useState<string | number | null>(null);
  const [editingDescriptionValue, setEditingDescriptionValue] = useState<string>("");

  // Store updated descriptions in state to survive refreshes
  const [updatedDescriptions, setUpdatedDescriptions] = useState<Record<string | number, string>>({});

  // Store ALL images with item-specific associations
  const [commonImagesState, setCommonImagesState] = useState<ImageData[]>([]);
  const [itemwiseImagesState, setItemwiseImagesState] = useState<Record<string | number, ImageData[]>>({});

  // File upload state
  const [openUploadDialog, setOpenUploadDialog] = useState<boolean>(false);
  const [selectedItemForUpload, setSelectedItemForUpload] = useState<ItemRow | null>(null);
  const [uploadMode, setUploadMode] = useState<"common" | "itemwise">("itemwise");
  const [fileName, setFileName] = useState<string>("");
  const [filePath, setFilePath] = useState<string>("");
  const [uploadError, setUploadError] = useState<string>("");
  const [byteArray, setByteArray] = useState<string>("");
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Refs for storing updated data when navigating back
  const updatedFormDataRef = useRef<any>(null);
  const updatedItemsRef = useRef<ItemRow[]>([]);
  const hasInitializedRef = useRef<boolean>(false);
  const initialDescriptionsAppliedRef = useRef<boolean>(false);
  const imagesLoadedRef = useRef<boolean>(false);

  // ✅ Guard ref to ensure common images are applied exactly once
  const hasAppliedCommonImagesRef = useRef<boolean>(false);

  // File input refs
  const commonFileInputRef = useRef<HTMLInputElement>(null);
  const itemWiseFileInputRef = useRef<HTMLInputElement>(null);
  const commonImagesLoadedForAddVariantRef = useRef<boolean>(false);

  // Track API calls and loading state
  const [apiCallCount, setApiCallCount] = useState<number>(0);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);

  // Confirmation dialog state
  const [openRemoveDialog, setOpenRemoveDialog] = useState<boolean>(false);
  const [itemToRemove, setItemToRemove] = useState<ItemRow | null>(null);
  const [openCancelDialog, setOpenCancelDialog] = useState<boolean>(false);
  const [cancelActionType, setCancelActionType] = useState<"cancel" | "back">("cancel");

  // Itemwise images management dialog state
  const [openManageImagesDialog, setOpenManageImagesDialog] = useState<boolean>(false);
  const [selectedItemForManage, setSelectedItemForManage] = useState<ItemRow | null>(null);

  // Sample image URL
  const sampleImageUrl = typeof sampleImage === 'string' ? sampleImage : (sampleImage as any).default || (sampleImage as any).src || sampleImage;

  // Image configuration
  const MAX_FILESIZE = 1 * 1024 * 1024; // 1MB

  // Debounced items to prevent excessive re-renders
  const debouncedItems = useDebounce(items, 300);

  // ✅ Derived flag: are we adding new variants to an already-saved item?
  // In that case, the previously saved common images must be read-only here.
  const inheritedReadOnlyCommonImages =
    fromCreateVariant || isEditMode;

  // Load initial updated descriptions from location state
  useEffect(() => {
    if (initialUpdatedDescriptions.length > 0 && !initialDescriptionsAppliedRef.current) {
      const descMap: Record<string | number, string> = {};
      initialUpdatedDescriptions.forEach(item => {
        descMap[item.id] = item.description;
      });
      setUpdatedDescriptions(descMap);
      initialDescriptionsAppliedRef.current = true;
      console.log("Loaded initial updated descriptions:", descMap);
    }
  }, [initialUpdatedDescriptions]);

  // ✅ FIX: Always keep the previously uploaded common images available,
  // so new variants can fall back to them even when Item-Specific Images is selected.
  // Also mark them as isExisting when they come from a previously saved item
  // (fromCreateVariant or isEditMode), so they become read-only in this view.
  useEffect(() => {
    if (
      commonImages &&
      commonImages.length > 0 &&
      !hasAppliedCommonImagesRef.current
    ) {
      const normalizedCommonImages: ImageData[] = commonImages.map((img: any) => ({
        ...img,
        // When we're creating new variants for an existing item, or updating
        // an existing item, the common images were already saved against the
        // base item — treat them as existing so the user cannot remove or
        // replace them here.
        isExisting:
          inheritedReadOnlyCommonImages
            ? true
            : (img?.isExisting ?? false),
      }));

      console.log(
        `✅ Applying existing common images from navigation state (imageType=${imageType}, fromCreateVariant=${fromCreateVariant}, isEditMode=${isEditMode}):`,
        normalizedCommonImages
      );
      setCommonImagesState(normalizedCommonImages);
      hasAppliedCommonImagesRef.current = true;
    }
  }, [commonImages, imageType, fromCreateVariant, isEditMode, inheritedReadOnlyCommonImages]);

  const getItemImageUrl = (item: ItemRow): string => {
    let images: ImageData[] = [];
    if (imageType === "common") {
      images = commonImagesState || [];
    } else if (imageType === "itemwise") {
      images = itemwiseImagesState[item.id] || [];

      // ✅ FIX: When the user selected Item-Specific Images but this variant
      // doesn't yet have its own image, fall back to the previously uploaded
      // common images (e.g. from the base item). This applies to every new row
      // that has no itemwise image yet — not just the fromCreateVariant flow.
      if (images.length === 0 && commonImagesState.length > 0) {
        images = commonImagesState;
      }
    }
    const defaultImage = images.find((img) => img.isDefault);
    const image = defaultImage || images[0];
    return image?.filepathUrl || image?.filepath || image?.data || sampleImageUrl;
  };

  const getVariantLines = (item: ItemRow): Array<{ label: string; value: string }> => {
    if (item.variantData && Object.keys(item.variantData).length > 0) {
      return Object.entries(item.variantData).map(([label, value]) => ({
        label,
        value: String(value ?? ""),
      }));
    }
    if (!item.variant || item.variant === "Standard") {
      return [{ label: "Variant", value: "None" }];
    }
    return item.variant.split(",").map((part) => {
      const [label, ...rest] = part.split(":");
      const value = rest.join(":").trim();
      return value
        ? { label: label.trim(), value }
        : { label: "Variant", value: part.trim() };
    });
  };

  const truncateDisplayText = (value: string, maxLength: number): { display: string; full: string; truncated: boolean } => {
    const full = String(value ?? "").trim();
    if (!full) return { display: "-", full: "", truncated: false };
    if (full.length <= maxLength) return { display: full, full, truncated: false };
    return { display: `${full.slice(0, maxLength)}...`, full, truncated: true };
  };

  const renderTruncatedText = (
    value: string,
    maxLength: number,
    sx: Record<string, any> = {}
  ): React.ReactElement => {
    const { display, full, truncated } = truncateDisplayText(value, maxLength);
    const content = (
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.875rem",
          lineHeight: 1.4,
          color: theme.palette.text.primary,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...sx,
        }}
      >
        {display}
      </Typography>
    );
    return truncated ? (
      <Tooltip title={full} arrow placement="top">
        <Box component="span" sx={{ display: "block", minWidth: 0, maxWidth: "100%" }}>
          {content}
        </Box>
      </Tooltip>
    ) : (
      content
    );
  };

  // Helper function to convert items back to variants format
  const convertItemsToVariants = useCallback((itemsList: ItemRow[]): any[] => {
    if (!itemsList || itemsList.length === 0) return [];

    // If it's a standard item with no variants
    if (itemsList.length === 1 && itemsList[0].variant === "Standard") {
      return [];
    }

    // Convert back to variant format
    const variantsMap = new Map();

    itemsList.forEach(item => {
      if (item.variantData) {
        Object.entries(item.variantData).forEach(([type, value]) => {
          if (!variantsMap.has(type)) {
            variantsMap.set(type, new Set());
          }
          variantsMap.get(type).add(value);
        });
      } else if (item.variant && item.variant !== "Standard") {
        // Parse variant string like "Color: Red, Size: Large"
        const variantParts = item.variant.split(',').map(part => part.trim());
        variantParts.forEach(part => {
          const [type, value] = part.split(':').map(s => s.trim());
          if (type && value) {
            if (!variantsMap.has(type)) {
              variantsMap.set(type, new Set());
            }
            variantsMap.get(type).add(value);
          }
        });
      }
    });

    // Convert map to array format
    const variantsArray: any[] = [];
    variantsMap.forEach((values, type) => {
      variantsArray.push({
        type,
        values: Array.from(values)
      });
    });

    return variantsArray;
  }, []);

  // Load existing item data for edit mode - MODIFIED to read isselling/isinv
  const loadExistingItemData = useCallback(async (): Promise<void> => {
    if (isEditMode && itemId && !dataLoaded) {
      try {
        setIsLoading(true);

        const response = await externalgetApi(ITEM_API_01, "GET", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
          itemcode: itemId,
        });

        if (response && Array.isArray(response)) {
          const matchingItems = response.filter((item: ApiResponseItem) =>
            String(item.itemcode) === String(itemId)
          );

          if (matchingItems.length > 0) {
            const firstItem = matchingItems[0] as ApiResponseItem;

            // ✅ Update items with isselling / isinv from API response
            setItems((prevItems) => {
              const updated = prevItems.map((item, index) => {
                const apiItem = matchingItems[index] || firstItem;

                // Parse isselling / isinv from API response
                const rawSelling = parseYNBool(apiItem.isselling, true);
                const rawInventory = parseYNBool(apiItem.isinv, false);
                // Enforce "at least one must be true"
                const { isSellingItem, isInventoryItem } =
                  enforceItemType(rawSelling, rawInventory);

                console.log(
                  `Item ${index + 1} (itemcode ${apiItem.itemcode}):`,
                  `isselling="${apiItem.isselling}" → isSellingItem=${isSellingItem}`,
                  `isinv="${apiItem.isinv}" → isInventoryItem=${isInventoryItem}`
                );

                return {
                  ...item,
                  isSellingItem,
                  isInventoryItem,
                  description: apiItem.descrip || item.description,
                };
              });
              updatedItemsRef.current = updated;
              return updated;
            });

            // Process images only - don't recreate items
            let allImages: ImageData[] = [];

            matchingItems.forEach((item: ApiResponseItem, itemIndex: number) => {
              let imageSource: string | null = null;
              let imageData: string | null = null;

              if (item.productimgs && item.productimgs !== "") {
                imageSource = "productimgs";
                imageData = item.productimgs;
              } else if (item.docpathjson && item.docpathjson !== "") {
                imageSource = "docpathjson";
                imageData = item.docpathjson;
              }

              if (imageData) {
                try {
                  let jsonString = imageData;
                  jsonString = jsonString.replace(/\\"/g, '"');
                  jsonString = jsonString.replace(/\\\\/g, "\\");

                  if (jsonString.startsWith('"') && jsonString.endsWith('"')) {
                    jsonString = jsonString.slice(1, -1);
                  }

                  let parsedImages: any[] = [];
                  try {
                    parsedImages = JSON.parse(jsonString);
                  } catch (parseError) {
                    try {
                      parsedImages = JSON.parse(imageData);
                    } catch (e2) {
                      if (imageData.startsWith('{')) {
                        parsedImages = [JSON.parse(imageData)];
                      } else if (imageData.includes('[')) {
                        const start = imageData.indexOf('[');
                        const end = imageData.lastIndexOf(']') + 1;
                        if (start >= 0 && end > start) {
                          const arrayPart = imageData.substring(start, end);
                          parsedImages = JSON.parse(arrayPart);
                        }
                      }
                    }
                  }

                  if (!Array.isArray(parsedImages)) {
                    parsedImages = [parsedImages];
                  }

                  parsedImages.forEach((img: any, imgIndex: number) => {
                    let imageUrl = "";
                    if (img.FILEPATH && (img.FILEPATH.startsWith('http') || img.FILEPATH.startsWith('data:'))) {
                      imageUrl = img.FILEPATH;
                    } else if (img.bytarr) {
                      imageUrl = `data:image/jpeg;base64,${img.bytarr}`;
                    }

                    if (imageUrl) {
                      const rawIsDefault =
                        img.ISDEFAULT ??
                        img.isdefault ??
                        img.IsDefault ??
                        img.rawData?.ISDEFAULT ??
                        img.rawData?.isdefault ??
                        false;

                      const isDefault = parseYNBool(rawIsDefault, false);

                      allImages.push({
                        id: Date.now() + itemIndex * 1000 + imgIndex,
                        data: imageUrl,
                        name: img.filename || img.FILEPATH?.split('/').pop() || `image_${itemIndex}_${imgIndex}.jpg`,
                        size: 0,
                        type: 'image/jpeg',
                        filename: img.filename || img.FILEPATH?.split('/').pop() || `image_${itemIndex}_${imgIndex}.jpg`,
                        base64Data: img.bytarr || "",
                        filepath: img.FILEPATH || "",
                        filepathUrl: img.FILEPATH || "",
                        doctypecode: img.DOCTYPECODE || "IMG",
                        doctypeothername: img.DOCTYPEOTHERNAME || "Item Image",
                        remark: img.REMARK || "",
                        variantIdentifier: img.REMARK || "",
                        rawData: img,
                        sourceItem: itemIndex,
                        isDefault: isDefault,
                        isExisting: true,
                      });
                    }
                  });
                } catch (parseError) {
                  console.error(`Error parsing ${imageSource} for item ${itemIndex + 1}:`, parseError);
                }
              }
            });

            if (imageType === 'common') {
              setCommonImagesState(allImages);
            } else if (imageType === 'itemwise') {
              const newItemwiseImages: Record<string | number, ImageData[]> = {};

              // Use existing items instead of recreating them
              items.forEach(item => {
                newItemwiseImages[item.id] = [];
              });

              allImages.forEach((image, index) => {
                let matchedItemId: string | number | null = null;
                const remark = image.remark?.toLowerCase() || "";

                items.forEach(item => {
                  const variantType = item.variantType?.toLowerCase() || "";
                  const variantKey = item.variantKey?.toLowerCase() || "";

                  if (remark.includes(variantType) || remark.includes(variantKey)) {
                    matchedItemId = item.id;
                  }
                });

                if (!matchedItemId && items.length > 0) {
                  matchedItemId = items[index % items.length].id;
                }

                if (matchedItemId) {
                  if (!newItemwiseImages[matchedItemId]) {
                    newItemwiseImages[matchedItemId] = [];
                  }
                  newItemwiseImages[matchedItemId].push(image);
                }
              });

              setItemwiseImagesState(newItemwiseImages);
            }
          }
        }

        setDataLoaded(true);
        setApiCallCount(prev => prev + 1);
        imagesLoadedRef.current = true;

      } catch (error: any) {
        console.error("Error loading existing item data:", error);
        setAlertType("error");
        setAlertMessage(`Failed to load data: ${error.message}`);
        setAlertOpen(true);
      } finally {
        setIsLoading(false);
      }
    }
  }, [isEditMode, itemId, imageType, COMPCODE, STAFFMAPLOCODE, dataLoaded, apiCallCount, items, updatedDescriptions]);

  // Initialize items from variants or variantCombinations - only run once
  useEffect(() => {
    // Skip if already initialized
    if (hasInitializedRef.current && items.length > 0) {
      return;
    }

    if (!isEditMode || items.length === 0) {
      if (variantCombinations && variantCombinations.length > 0) {
        const generatedItems: ItemRow[] = variantCombinations.map((combo, index) => {
          const variantString = Object.entries(combo).map(([key, value]) => `${key}: ${value}`).join(", ");
          const effectiveItemCode = fromCreateVariant ? 0 : (itemId || 0);
          const itemId_num = index + 1;

          // Check if we have an updated description for this item
          const updatedDesc = updatedDescriptions[itemId_num] || initialUpdatedDescriptions.find(d => d.id === itemId_num)?.description;

          return {
            id: itemId_num,
            itemcode: effectiveItemCode,
            itemname: formData?.itemName || "Item",
            variant: variantString || "Standard",
            description: updatedDesc || formData?.description,
            createddate: new Date().toLocaleDateString(),
            uploadtype: imageType === 'common' ? 'Common' : 'Item-wise',
            variantData: combo,
            variantKey: Object.keys(combo)[0]?.toLowerCase() || "standard",
            variantType: Object.keys(combo)[0] || "Standard",
            isSellingItem: seedSelling,
            isInventoryItem: seedInventory,
          };
        });

        setItems(generatedItems);
        setFilteredItems(generatedItems);
        updatedItemsRef.current = generatedItems;
        hasInitializedRef.current = true;

        if (imageType === 'itemwise') {
          const newItemwiseImages: Record<string | number, ImageData[]> = {};
          generatedItems.forEach(item => {
            newItemwiseImages[item.id] = [];
          });
          setItemwiseImagesState(newItemwiseImages);
        }
      } else if (variants && variants.length > 0) {
        generateItemCombinations();
        hasInitializedRef.current = true;
      } else {
        const singleItemCode = fromCreateVariant ? 0 : (itemId || 0);

        // Check if we have an updated description for the single item
        const updatedDesc = updatedDescriptions[1] || initialUpdatedDescriptions.find(d => d.id === 1)?.description;

        const singleItem: ItemRow = {
          id: 1,
          itemcode: singleItemCode,
          itemname: formData?.itemName || "Item",
          variant: "Standard",
          description: updatedDesc || formData?.description || "Item Description",
          createddate: new Date().toLocaleDateString(),
          uploadtype: imageType === 'common' ? 'Common' : 'Item-wise',
          variantKey: "standard",
          isSellingItem: seedSelling,
          isInventoryItem: seedInventory,
        };

        setItems([singleItem]);
        setFilteredItems([singleItem]);
        updatedItemsRef.current = [singleItem];
        hasInitializedRef.current = true;

        if (imageType === 'itemwise') {
          setItemwiseImagesState(prev => ({
            ...prev,
            1: prev[1] || []
          }));
        }
      }
    }
  }, [variants, variantCombinations, formData, imageType, isEditMode, itemId, fromCreateVariant, updatedDescriptions, initialUpdatedDescriptions, selectedItem, savedItemData, seedSelling, seedInventory]);

  const generateItemCombinations = (): void => {
    if (variants && variants.length > 0) {
      const generate = (index: number, current: Record<string, string>): Record<string, string>[] => {
        if (index === variants.length) {
          return [current];
        }

        const variant = variants[index];
        const results: Record<string, string>[] = [];

        for (const value of variant.values) {
          const newCurrent = {
            ...current,
            [variant.type]: value
          };
          results.push(...generate(index + 1, newCurrent));
        }

        return results;
      };

      const combinations = generate(0, {});

      const generatedItems: ItemRow[] = combinations.map((combo, index) => {
        const variantString = Object.entries(combo).map(([key, value]) => `${key}: ${value}`).join(", ");
        const variantKey = Object.keys(combo)[0]?.toLowerCase() || "standard";
        const variantType = Object.keys(combo)[0] || "Standard";
        const itemId_num = index + 1;

        const itemCode = fromCreateVariant
          ? 0
          : (isEditMode && itemId
            ? itemId
            : (formData?.itemName
              ? `${formData.itemName.replace(/\s+/g, '-').toUpperCase()}-${String(index + 1).padStart(3, '0')}`
              : `ITEM-${String(index + 1).padStart(3, '0')}`));

        // Check if we have an updated description for this item
        const updatedDesc = updatedDescriptions[itemId_num] || initialUpdatedDescriptions.find(d => d.id === itemId_num)?.description;

        return {
          id: itemId_num,
          itemcode: itemCode,
          itemname: formData?.itemName || "Item",
          variant: variantString,
          description: updatedDesc || formData?.description || "Item Description",
          createddate: new Date().toLocaleDateString(),
          uploadtype: imageType === 'common' ? 'Common' : 'Item-wise',
          variantData: combo,
          variantKey: variantKey,
          variantType: variantType,
          isSellingItem: seedSelling,
          isInventoryItem: seedInventory,
        };
      });

      setItems(generatedItems);
      setFilteredItems(generatedItems);
      updatedItemsRef.current = generatedItems;
      hasInitializedRef.current = true;

      if (imageType === 'itemwise') {
        const newItemwiseImages: Record<string | number, ImageData[]> = {};
        generatedItems.forEach(item => {
          if (!newItemwiseImages[item.id]) {
            newItemwiseImages[item.id] = [];
          }
        });
        setItemwiseImagesState(newItemwiseImages);
      }
    }
  };

  // Handle search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredItems(items);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = items.filter(item =>
      (item.itemname?.toLowerCase().includes(query)) ||
      (String(item.itemcode).toLowerCase().includes(query)) ||
      (item.variant?.toLowerCase().includes(query)) ||
      (item.description?.toLowerCase().includes(query))
    );

    setFilteredItems(filtered);
  }, [searchQuery, items]);

  // Helper function to get token
  const getToken = async (): Promise<string | null> => {
    try {
      const userPersist = localStorage.getItem("persist:user");
      if (userPersist) {
        const parsedUser = JSON.parse(userPersist);
        if (parsedUser.USER_PARAM) {
          const userParam = JSON.parse(parsedUser.USER_PARAM);
          if (userParam?.TOKEN) {
            return userParam.TOKEN;
          }
        }
      }
    } catch (error) {
      console.error("Error retrieving token from localStorage:", error);
    }
    return null;
  };

  // Handle image upload confirmation with API upload
  const handleUploadConfirm = async (): Promise<void> => {
    const fileInput = itemWiseFileInputRef.current;
    if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
      setUploadError("Please select at least one file");
      return;
    }

    const fileArray = Array.from(fileInput.files);

    for (const file of fileArray) {
      if (file.size > MAX_FILESIZE) {
        setAlertType("error");
        setAlertMessage(`File "${file.name}" size must be less than or equal to 1MB.`);
        setAlertOpen(true);
        return;
      }
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError("");

    const uploadedImages: ImageData[] = [];
    let successCount = 0;
    let failCount = 0;

    try {
      const token = await getToken();

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];

        try {
          const validationResult = await validateImageSpecs(file);

          if (!validationResult.isValid) {
            failCount++;
            continue;
          }

          const formData = new FormData();
          formData.append('file', file);

          const response = await axios.post<UploadResponse>(
            `${ENDPOINT}${UPLOAD_API}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: token ? `Bearer ${token}` : "",
              },
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                  const percentCompleted = Math.round(
                    ((progressEvent.loaded + i * progressEvent.total) * 100) /
                    (progressEvent.total * fileArray.length)
                  );
                  setUploadProgress(percentCompleted);
                }
              },
            }
          );

          if (response.data && response.data.filepath) {
            const newImage: ImageData = {
              id: Date.now() + i,
              data: URL.createObjectURL(file),
              name: file.name,
              size: file.size,
              type: file.type,
              filename: file.name,
              filepath: response.data.filepath,
              filepathUrl: response.data.filepath,
              variantIdentifier: uploadMode === 'itemwise' && selectedItemForUpload
                ? selectedItemForUpload.variant
                : "Common Image",
              isDefault: false,
              isExisting: false,
            };
            uploadedImages.push(newImage);
            successCount++;
          }
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          failCount++;
        }
      }

      if (uploadedImages.length > 0) {
        if (uploadMode === 'common') {
          setCommonImagesState((prev) => {
            const isFirstUpload = prev.length === 0;
            const updatedImages = uploadedImages.map((img, index) => ({
              ...img,
              isDefault: isFirstUpload && index === 0 ? true : false,
            }));
            return [...prev, ...updatedImages];
          });
          setAlertType("success");
          setAlertMessage(
            `${successCount} common image(s) uploaded successfully${failCount > 0 ? `, ${failCount} failed` : ''}.`
          );
        } else if (uploadMode === 'itemwise' && selectedItemForUpload) {
          setItemwiseImagesState((prev) => {
            const existingImages = prev[selectedItemForUpload.id] || [];
            const isFirstUpload = existingImages.length === 0;
            const updatedImages = uploadedImages.map((img, index) => ({
              ...img,
              isDefault: isFirstUpload && index === 0 ? true : false,
            }));
            return {
              ...prev,
              [selectedItemForUpload.id]: [...existingImages, ...updatedImages]
            };
          });
          setAlertType("success");
          setAlertMessage(
            `${successCount} image(s) uploaded for ${selectedItemForUpload.itemname} (${selectedItemForUpload.variant})${failCount > 0 ? `, ${failCount} failed` : ''}!`
          );
        }

        setFileName("");
        setFilePath("");
        setByteArray("");
        setSelectedFile(null);
        setOpenUploadDialog(false);
        setAlertOpen(true);
      } else {
        setUploadError("Image upload failed. Please upload an image that meets the required guidelines and try again.");
        setAlertType("error");
        setAlertMessage("Image upload failed. Please upload an image that meets the required guidelines and try again.");
        setAlertOpen(true);
      }
    } catch (error: any) {
      console.error("Error uploading images:", error);
      setUploadError(error.response?.data?.message || error.message || "Failed to upload images. Please try again.");
      setAlertType("error");
      setAlertMessage(error.response?.data?.message || error.message || "Failed to upload images. Please try again.");
      setAlertOpen(true);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (itemWiseFileInputRef.current) {
        itemWiseFileInputRef.current.value = '';
      }
    }
  };

  // Open upload dialog for specific item
  const handleOpenUploadDialog = (item: ItemRow): void => {
    setItemwiseImagesState((prev) =>
      prev[item.id] ? prev : { ...prev, [item.id]: [] }
    );
    setSelectedItemForUpload(item);
    setUploadMode("itemwise");
    setFileName("");
    setFilePath("");
    setByteArray("");
    setUploadError("");
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setOpenUploadDialog(true);
  };

  // Handle common image upload via file input
  const handleCommonImageUpload = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    for (const file of fileArray) {
      if (!file.type.startsWith('image/')) {
        setAlertType("error");
        setAlertMessage("All files must be images. Please upload image files only.");
        setAlertOpen(true);
        return;
      }

      if (file.size > MAX_FILESIZE) {
        setAlertType("error");
        setAlertMessage(`File "${file.name}" is too large. Maximum size is 1MB.`);
        setAlertOpen(true);
        return;
      }
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError("");

    const uploadedImages: ImageData[] = [];
    let successCount = 0;
    let failCount = 0;

    try {
      const token = await getToken();

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];

        try {
          const validationResult = await validateImageSpecs(file);

          if (!validationResult.isValid) {
            failCount++;
            continue;
          }

          const formData = new FormData();
          formData.append('file', file);

          const response = await axios.post<UploadResponse>(
            `${ENDPOINT}${UPLOAD_API}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: token ? `Bearer ${token}` : "",
              },
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                  const percentCompleted = Math.round(
                    ((progressEvent.loaded + i * progressEvent.total) * 100) /
                    (progressEvent.total * fileArray.length)
                  );
                  setUploadProgress(percentCompleted);
                }
              },
            }
          );

          if (response.data && response.data.filepath) {
            const newImage: ImageData = {
              id: Date.now() + i,
              data: URL.createObjectURL(file),
              name: file.name,
              size: file.size,
              type: file.type,
              filename: file.name,
              filepath: response.data.filepath,
              filepathUrl: response.data.filepath,
              variantIdentifier: "Common Image",
              isDefault: false,
              isExisting: false,
            };
            uploadedImages.push(newImage);
            successCount++;
          }
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          failCount++;
        }
      }

      if (uploadedImages.length > 0) {
        setCommonImagesState((prev) => {
          const isFirstUpload = prev.length === 0;
          const updatedImages = uploadedImages.map((img, index) => ({
            ...img,
            isDefault: isFirstUpload && index === 0 ? true : false,
          }));
          return [...prev, ...updatedImages];
        });
        setAlertType("success");
        setAlertMessage(
          `${successCount} image(s) uploaded successfully${failCount > 0 ? `, ${failCount} failed` : ''}.`
        );
        setAlertOpen(true);
      } else {
        setAlertType("error");
        setAlertMessage("Image upload failed. Please upload an image that meets the required guidelines and try again.");
        setAlertOpen(true);
      }
    } catch (error: any) {
      console.error("Error uploading images:", error);
      setAlertType("error");
      setAlertMessage("Failed to upload images. Please try again.");
      setAlertOpen(true);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (commonFileInputRef.current) {
        commonFileInputRef.current.value = '';
      }
    }
  };

  const applyItemPatch = (itemId: string | number, patch: Partial<ItemRow>): void => {
    const patchList = (list: ItemRow[]) =>
      list.map((item) => (item.id === itemId ? { ...item, ...patch } : item));
    setItems((prev) => {
      const next = patchList(prev);
      updatedItemsRef.current = next;
      return next;
    });
    setFilteredItems((prev) => patchList(prev));
  };

  const saveItemDescription = (itemId: string | number, description: string): void => {
    setUpdatedDescriptions((prev) => ({ ...prev, [itemId]: description }));
    applyItemPatch(itemId, { description });
    if (items.length === 1) {
      updatedFormDataRef.current = { ...formData, description };
    }
    setEditingDescriptionId(null);
  };

  const handleItemTypeToggle = (
    item: ItemRow,
    field: "selling" | "inventory"
  ): void => {
    const selling = item.isSellingItem !== false;
    const inventory = item.isInventoryItem === true;
    const nextSelling = field === "selling" ? !selling : selling;
    const nextInventory = field === "inventory" ? !inventory : inventory;
    if (!nextSelling && !nextInventory) {
      setAlertType("warning");
      setAlertMessage("Please select at least one item type.");
      setAlertOpen(true);
      return;
    }
    applyItemPatch(item.id, {
      isSellingItem: nextSelling,
      isInventoryItem: nextInventory,
    });
  };

  // Handle remove item confirmation
  const handleRemoveItemClick = (itemId: string | number): void => {
    // Add validation to prevent removing the last remaining item
    if (items.length <= 1) {
      setAlertType("warning");
      setAlertMessage("Cannot remove the last variant. At least one item is required.");
      setAlertOpen(true);
      return;
    }

    if (isEditMode && imageType === "itemwise") {
      const rowImages = itemwiseImagesState[itemId] || [];
      const hasExistingImages = rowImages.some((img) => img?.isExisting);
      if (hasExistingImages) {
        setAlertType("warning");
        setAlertMessage("This variant has existing images, so it cannot be removed in edit mode.");
        setAlertOpen(true);
        return;
      }
    }

    const item = items.find(i => i.id === itemId);
    if (item) {
      setItemToRemove(item);
      setOpenRemoveDialog(true);
    }
  };

  const confirmRemoveItem = (): void => {
    if (itemToRemove) {
      if (isEditMode && imageType === "itemwise") {
        const rowImages = itemwiseImagesState[itemToRemove.id] || [];
        const hasExistingImages = rowImages.some((img) => img?.isExisting);
        if (hasExistingImages) {
          setAlertType("warning");
          setAlertMessage("This variant has existing images, so it cannot be removed in edit mode.");
          setAlertOpen(true);
          setOpenRemoveDialog(false);
          setItemToRemove(null);
          return;
        }
      }

      const updatedItems = items.filter((item) => item.id !== itemToRemove.id);
      setItems(updatedItems);
      setFilteredItems(updatedItems);
      updatedItemsRef.current = updatedItems;

      if (imageType === 'itemwise' && itemwiseImagesState[itemToRemove.id]) {
        const updatedItemwiseImages = { ...itemwiseImagesState };
        delete updatedItemwiseImages[itemToRemove.id];
        setItemwiseImagesState(updatedItemwiseImages);
      }

      setAlertType("success");
      setAlertMessage(`Product "${itemToRemove.itemname} - ${itemToRemove.variant}" removed successfully.`);
      setAlertOpen(true);
    }
    setOpenRemoveDialog(false);
    setItemToRemove(null);
  };

  const cancelRemoveItem = (): void => {
    setOpenRemoveDialog(false);
    setItemToRemove(null);
  };

  // Handle setting default image for common images
  // ✅ FIX: Block changing the default of inherited (previously saved) common
  // images when adding new variants to an existing item / editing an existing item.
  const handleSetDefaultCommonImage = (imageId: string | number): void => {
    setCommonImagesState((prev) => {
      const targetImage = prev.find((img) => img.id === imageId);
      if (inheritedReadOnlyCommonImages && targetImage?.isExisting) {
        setAlertType("warning");
        setAlertMessage(
          fromCreateVariant
            ? "Common images inherited from the base item cannot be modified while adding new variants."
            : "Existing images cannot be modified in edit mode."
        );
        setAlertOpen(true);
        return prev;
      }
      return prev.map((img) => ({
        ...img,
        isDefault: img.id === imageId ? true : false,
      }));
    });
  };

  // Handle setting default image for itemwise images
  const handleSetDefaultItemwiseImage = (
    itemId: string | number,
    imageId: string | number
  ): void => {
    setItemwiseImagesState((prev) => {
      const images = prev[itemId] || [];

      const updatedImages = images.map((image) => ({
        ...image,
        isDefault: String(image.id) === String(imageId),
        rawData: {
          ...(image.rawData || {}),
          ISDEFAULT: String(image.id) === String(imageId) ? 1 : 0,
        },
      }));

      return {
        ...prev,
        [itemId]: updatedImages,
      };
    });
  };

  // Handle removing image from common images
  // ✅ FIX: Block removal of previously saved common images, both in edit mode
  // and when adding new variants to an existing item.
  const handleRemoveCommonImage = (imageId: string | number): void => {
    setCommonImagesState((prev) => {
      const targetImage = prev.find((img) => img.id === imageId);
      if (inheritedReadOnlyCommonImages && targetImage?.isExisting) {
        setAlertType("warning");
        setAlertMessage(
          fromCreateVariant
            ? "Common images inherited from the base item cannot be removed while adding new variants."
            : "Existing images cannot be deleted in edit mode."
        );
        setAlertOpen(true);
        return prev;
      }
      return prev.filter((img) => img.id !== imageId);
    });
  };

  // Handle removing image from itemwise images
  const handleRemoveItemwiseImage = (itemId: string | number, imageId: string | number): void => {
    setItemwiseImagesState((prev) => ({
      ...prev,
      [itemId]: (prev[itemId] || []).filter((img) => {
        if (img.id !== imageId) return true;
        if (isEditMode && img?.isExisting) {
          setAlertType("warning");
          setAlertMessage("Existing images cannot be deleted in edit mode.");
          setAlertOpen(true);
          return true;
        }
        return false;
      }),
    }));
  };

  const handleClearAllCommonImages = (): void => {
    setCommonImagesState((prev) => {
      // ✅ FIX: When adding new variants to an existing item, inherited common
      // images must not be cleared — only newly added ones can be removed.
      if (fromCreateVariant) {
        const retainedExistingImages = prev.filter((img) => img?.isExisting);
        if (retainedExistingImages.length === prev.length) {
          setAlertType("warning");
          setAlertMessage(
            "Common images inherited from the base item cannot be removed while adding new variants."
          );
          setAlertOpen(true);
          return prev;
        }
        setAlertType("info");
        setAlertMessage("Only newly added images were cleared. Inherited common images were kept.");
        setAlertOpen(true);
        return retainedExistingImages;
      }

      if (!isEditMode) return [];

      const retainedExistingImages = prev.filter((img) => img?.isExisting);
      if (retainedExistingImages.length === prev.length) {
        setAlertType("warning");
        setAlertMessage("Existing images cannot be deleted in edit mode.");
        setAlertOpen(true);
        return prev;
      }

      setAlertType("info");
      setAlertMessage("Only newly added images were cleared. Existing images were kept.");
      setAlertOpen(true);
      return retainedExistingImages;
    });
  };

  // Open manage images dialog for itemwise images
  const handleOpenManageImagesDialog = (item: ItemRow): void => {
    setItemwiseImagesState((prev) =>
      prev[item.id] ? prev : { ...prev, [item.id]: [] }
    );
    setSelectedItemForManage(item);
    setOpenManageImagesDialog(true);
  };

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // Prepare payload with items array structure for CreateItems API
  const preparePayload = (): any => {
    const itemsArray: any[] = [];
    const currentDate = new Date().toISOString().split('T')[0];

    items.forEach((item) => {
      const productItemvariants: any[] = [];
      const payloadItemCode = fromCreateVariant ? 0 : (item.itemcode || itemId || 0);

      if (item.variantData && Object.keys(item.variantData).length > 0) {
        Object.entries(item.variantData).forEach(([type, value], index) => {
          productItemvariants.push({
            itemcode: payloadItemCode,
            variantcode: index + 1,
            variantvalue: value,
            descrip: `${type}: ${value}`,
            itemimage: "",
          });
        });
      } else {
        productItemvariants.push({
          itemcode: payloadItemCode,
          variantcode: 0,
          variantvalue: "Standard",
          descrip: "Standard item",
          itemimage: "",
        });
      }

      const dtoDOCs: any[] = [];
      const productimgsArray: any[] = [];

      // FIX: Sorting arrays so that the default image is always placed at the 0th index before creating the payload
      if (imageType === 'common' && commonImagesState.length > 0) {
        const sortedImages = [...commonImagesState].sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1));
        sortedImages.forEach((image) => {
          const imageUrl = image.filepathUrl || image.filepath || image.data || "";
          if (!imageUrl) return;
          dtoDOCs.push({
            doctypecode: image.doctypecode || "",
            doctypeothername: image.doctypeothername || "",
            docpathjson: imageUrl,
            filename: image.filename || imageUrl,
            bytarr: image.base64Data || "",
            remark: image.remark || `Common image for ${item.itemname} - ${item.variant} - Uploaded on ${currentDate}`,
          });
          productimgsArray.push({
            FILEPATH: imageUrl,
            DOCTYPECODE: image.doctypecode || "",
            DOCTYPEOTHERNAME: image.doctypeothername || "",
            REMARK: image.remark || `Common image for ${item.itemname} - ${item.variant} - Uploaded on ${currentDate}`,
            DOCPATH: image.rawData?.DOCPATH ?? image.rawData?.docpath ?? null,
            ISDEFAULT: image.isDefault ? 1 : 0,
          });
        });
      } else if (imageType === 'itemwise' && itemwiseImagesState[item.id] && itemwiseImagesState[item.id].length > 0) {
        const sortedImages = [...itemwiseImagesState[item.id]].sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1));
        sortedImages.forEach((image) => {
          const imageUrl = image.filepathUrl || image.filepath || image.data || "";
          if (!imageUrl) return;
          dtoDOCs.push({
            doctypecode: image.doctypecode || "",
            doctypeothername: image.doctypeothername || "",
            docpathjson: imageUrl,
            filename: image.filename || imageUrl,
            bytarr: image.base64Data || "",
            remark: image.remark || `Image for ${item.itemname} - ${item.variant} - Uploaded on ${currentDate}`,
          });
          productimgsArray.push({
            FILEPATH: imageUrl,
            DOCTYPECODE: image.doctypecode || "",
            DOCTYPEOTHERNAME: image.doctypeothername || "",
            REMARK: image.remark || `Image for ${item.itemname} - ${item.variant} - Uploaded on ${currentDate}`,
            DOCPATH: image.rawData?.DOCPATH ?? image.rawData?.docpath ?? null,
            ISDEFAULT: image.isDefault ? 1 : 0,
          });
        });
      }
      // ✅ FIX: Variants with no itemwise images inherit the common images
      else if (
        imageType === 'itemwise' &&
        commonImagesState.length > 0
      ) {
        const sortedImages = [...commonImagesState].sort((a, b) =>
          a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1
        );
        sortedImages.forEach((image) => {
          const imageUrl = image.filepathUrl || image.filepath || image.data || "";
          if (!imageUrl) return;
          dtoDOCs.push({
            doctypecode: image.doctypecode || "",
            doctypeothername: image.doctypeothername || "",
            docpathjson: imageUrl,
            filename: image.filename || imageUrl,
            bytarr: image.base64Data || "",
            remark:
              image.remark ||
              `Inherited common image for ${item.itemname} - ${item.variant} - ${currentDate}`,
          });
          productimgsArray.push({
            FILEPATH: imageUrl,
            DOCTYPECODE: image.doctypecode || "",
            DOCTYPEOTHERNAME: image.doctypeothername || "",
            REMARK:
              image.remark ||
              `Inherited common image for ${item.itemname} - ${item.variant} - ${currentDate}`,
            DOCPATH: image.rawData?.DOCPATH ?? image.rawData?.docpath ?? null,
            ISDEFAULT: image.isDefault ? 1 : 0,
          });
        });
      }

      let variantdataJsonString = "[]";
      if (item.variantData && Object.keys(item.variantData).length > 0) {
        const variantdataArray = Object.entries(item.variantData).map(([variantname, variantvalue], index) => ({
          variantcode: index + 1,
          variantname: variantname || "",
          variantvalue: variantvalue || ""
        }));
        variantdataJsonString = JSON.stringify(variantdataArray);
      } else {
        variantdataJsonString = JSON.stringify([{
          variantcode: 0,
          variantname: "",
          variantvalue: "Standard"
        }]);
      }

      // Defensive: enforce at least one item type true at payload build time
      const safeSelling = item.isSellingItem !== false;
      const safeInventory = item.isInventoryItem === true;
      const { isSellingItem: finalSelling, isInventoryItem: finalInventory } =
        enforceItemType(safeSelling, safeInventory);

      const itemEntry = {
        productItemvariants,
        dtoDOCs: dtoDOCs.length > 0 ? dtoDOCs : [],
        businesstypecode: "O",
        itemcode: payloadItemCode,
        compcode: COMPCODE || "",
        loccode: STAFFMAPLOCODE || "",
        maincatcode: formData?.mainCategory || selectedItem?.maincatcode || savedItemData?.maincatcode || 0,
        catcode: formData?.category || selectedItem?.catcode || savedItemData?.catcode || 0,
        subcatcode: formData?.subCategory || selectedItem?.subcatcode || savedItemData?.subcatcode || 0,
        genericcode: formData?.genericName || selectedItem?.genericcode || savedItemData?.genericcode || 0,
        packagecode: formData?.packageType || selectedItem?.packagecode || savedItemData?.packagecode || 0,
        measuringunitcode: formData?.uom || selectedItem?.measuringunitcode || savedItemData?.measuringunitcode || 0,
        brandcode: formData?.brand || selectedItem?.brandcode || savedItemData?.brandcode || 0,
        itemname: item.itemname || formData?.itemName || selectedItem?.itemname || "",
        modelname: formData?.modelName || selectedItem?.modelname || "",
        packageunits: Number(formData?.unitsPerPackage) || selectedItem?.packageunits || 0,
        descrip: item.description || formData?.description || selectedItem?.descrip || "",
        isselling: finalSelling ? "Y" : "N",
        isinv: finalInventory ? "Y" : "N",
        variantdata: variantdataJsonString,
        productimgs: productimgsArray.length > 0 ? JSON.stringify(productimgsArray) : "",
        sts: CON_STATUS_ACTIVE,
        enuser: USERNAME || "Admin",
        endate: currentDate,
      };

      itemsArray.push(itemEntry);
    });

    return { items: itemsArray };
  };

  // Handle final save/update
  const handleUpdate = async (): Promise<void> => {
    if (isSaving) {
      console.log("Update already in progress, ignoring duplicate call");
      return;
    }

    // Pre-submit validation: every row must have at least one item type selected
    const invalidRows = items.filter(
      (item) => item.isSellingItem === false && item.isInventoryItem === false
    );
    if (invalidRows.length > 0) {
      setAlertType("warning");
      setAlertMessage(
        `Please select at least one item type (Selling Item or Inventory Item) for: ${invalidRows
          .map((r) => `${r.itemname}${r.variant && r.variant !== "Standard" ? ` (${r.variant})` : ""}`)
          .join(", ")}`
      );
      setAlertOpen(true);
      return;
    }

    setIsSaving(true);

    try {
      const payload = preparePayload();

      if (!payload.items || payload.items.length === 0) {
        setAlertType("info");
        setAlertMessage("No products to save.");
        setAlertOpen(true);
        setIsSaving(false);
        return;
      }

      const response = await externalpostApi(ITEM_CREATE_ITEMS_API, payload);

      if (response !== null && response !== undefined) {
        setAlertType("success");
        setAlertMessage(`${payload.items.length} product(s) ${isEditMode ? 'updated' : 'created'} successfully!`);
        setAlertOpen(true);

        setTimeout(() => {
          navigate("/items");
        }, 2000);
      } else {
        throw new Error("Failed to save products");
      }
    } catch (error: any) {
      console.error("Error saving item images:", error);

      let errorMessage = "An error occurred while saving images";
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.statusText || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setAlertType("error");
      setAlertMessage(`Error: ${errorMessage}`);
      setAlertOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = (): void => {
    // Before going back, update the formData with the latest descriptions from all items
    const updatedFormData = { ...formData };

    // If there's only one item, update the description directly
    if (items.length === 1) {
      updatedFormData.description = items[0].description;
    } else {
      // For multiple items, store the first item's description as the main description
      updatedFormData.description = items[0]?.description || formData?.description || "";
    }

    setCancelActionType("back");
    setOpenCancelDialog(true);

    // Store the updated form data in a ref to use when confirming
    updatedFormDataRef.current = updatedFormData;
    updatedItemsRef.current = items;
  };

  const handleCancel = (): void => {
    setCancelActionType("cancel");
    setOpenCancelDialog(true);
  };

  const confirmCancel = (): void => {
    setOpenCancelDialog(false);

    if (cancelActionType === "back") {
      // Use the updated form data and items if available
      const navigationFormData = updatedFormDataRef.current || formData;
      const navigationItems = updatedItemsRef.current.length > 0 ? updatedItemsRef.current : items;

      // Convert items back to variants format if needed
      const updatedVariants = convertItemsToVariants(navigationItems);

      // Prepare updated item descriptions to pass back
      const updatedItemDescriptions = navigationItems.map(item => ({
        id: item.id,
        description: item.description
      }));

      console.log("Navigating back with updated descriptions:", updatedItemDescriptions);

      if (locationState?.fromCreateVariant) {
        navigate("/vCreateVariant", {
          state: {
            selectedItem: selectedItem || locationState?.selectedItem,
            returnFromItemImages: true,
            formData: navigationFormData,
            variants: updatedVariants,
            variantCombinations: locationState?.variantCombinations ?? [],
            imageType,
            updatedItemDescriptions,
          },
        });
      } else {
        navigate("/vCreateItem", {
          state: {
            formData: navigationFormData,
            variants: updatedVariants,
            variantCombinations: locationState?.variantCombinations ?? [],
            combinationCount,
            commonImages: commonImagesState,
            itemwiseImages: itemwiseImagesState,
            imageType,
            itemId,
            isEditMode,
            selectedItem,
            savedItemData,
            actionText: isEditMode ? "Edit" : "Add",
            returnFromItemImages: true,
            updatedItemDescriptions,
          },
        });
      }
    } else {
      navigate("/items");
    }

    // Clear refs
    updatedFormDataRef.current = null;
    updatedItemsRef.current = [];
  };

  const cancelCancel = (): void => {
    setOpenCancelDialog(false);
  };

  // Handle file input change for dialog (supports multiple files)
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      setFileName("");
      setUploadError("");
      setSelectedFile(null);
      return;
    }

    const fileArray = Array.from(files);

    for (const file of fileArray) {
      if (!file.type.startsWith('image/')) {
        setUploadError("All files must be image files");
        setFileName("");
        setSelectedFile(null);
        return;
      }

      if (file.size > MAX_FILESIZE) {
        setUploadError(`File "${file.name}" size must be less than 1MB`);
        setFileName("");
        setSelectedFile(null);
        return;
      }
    }

    setSelectedFile(fileArray[0]);
    setFileName(fileArray.length === 1 ? fileArray[0].name : `${fileArray.length} files selected`);
    setUploadError("");
  };

  // Load existing data when items are ready
  useEffect(() => {
    let isMounted = true;
    let loadTimeout: NodeJS.Timeout | null = null;

    const loadData = async () => {
      if (isEditMode && itemId && debouncedItems.length > 0 && !dataLoaded && !imagesLoadedRef.current) {
        if (loadTimeout) clearTimeout(loadTimeout);

        loadTimeout = setTimeout(async () => {
          if (isMounted) {
            await loadExistingItemData();
          }
        }, 100);
      }
    };

    loadData();

    return () => {
      isMounted = false;
      if (loadTimeout) clearTimeout(loadTimeout);
    };
  }, [isEditMode, itemId, debouncedItems.length, dataLoaded, loadExistingItemData]);

  // Reset dataLoaded when itemId changes
  useEffect(() => {
    if (itemId && isEditMode) {
      setDataLoaded(false);
      setCommonImagesState([]);
      setItemwiseImagesState({});
      imagesLoadedRef.current = false;
    }
  }, [itemId, isEditMode]);

  // ✅ Derived: are ALL current common images inherited (read-only)?
  const allCommonImagesReadOnly =
    inheritedReadOnlyCommonImages &&
    commonImagesState.length > 0 &&
    commonImagesState.every((img) => img?.isExisting);

  return (
    <>
      {/* Alert System */}
      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />

      <Box sx={styles.box_page_container}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Tooltip title="Back to Create Item" arrow>
            <IconButton
              sx={styles.btn_breadcrumb_back}
              onClick={handleBack}
            >
              <ArrowBackIcon sx={styles.btn_breadcrumb_back_icon} />
            </IconButton>
          </Tooltip>
          <Typography sx={styles.breadcrumb_divider} ml={1}>|</Typography>
          <Breadcrumbs separator="›" sx={styles.breadcrumb}>
            <Link underline="hover" sx={styles.breadcrumb_link}>Home</Link>
            <Link underline="hover" sx={styles.breadcrumb_link}>Item Management</Link>
            <Link underline="hover" sx={styles.breadcrumb_link}>Create Items</Link>
            <Typography sx={styles.breadcrumb_link_page}>
              {isEditMode ? 'Update Item Images' : 'Upload Item Images'}
            </Typography>
          </Breadcrumbs>
        </Box>

        <AppBar sx={styles.appbar}>
          <Grid2 container spacing={2} sx={styles.appbar_grid_container}>
            <Grid2
              size={{ xs: 12, sm: 12, md: 6, lg: 8, xl: 8 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                Upload Images & Verify Variants
              </Typography>
            </Grid2>
          </Grid2>
        </AppBar>

        <Box sx={styles.view_page_container}>
          {/* Common Image Upload Section */}
          {imageType === 'common' && (
            <Box sx={{
              mb: 3,
              p: 3,
              border: `1px solid ${theme.palette.greyscale?.stroke_1}`,
              borderRadius: 1,
              backgroundColor: theme.palette.background.paper,
              boxShadow: `0px 1px 2px rgba(0, 0, 0, 0.1)`
            }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  mb: fromCreateVariant ? 1 : 3,
                  color: theme.palette.text.primary,
                  fontSize: theme.typography.h6?.fontSize,
                  fontWeight: theme.typography.h6?.fontWeight,
                }}
              >
                Add Common Images for All Items
              </Typography>

              {/* ✅ FIX: Explain to the user that previously saved common images are read-only */}
              {fromCreateVariant && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 3,
                    color: theme.palette.text.secondary,
                    fontSize: "0.875rem",
                    fontStyle: "italic",
                  }}
                >
                  
                </Typography>
              )}

              <Box sx={{ mb: 3 }}>
                <input
                  type="file"
                  ref={commonFileInputRef}
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => handleCommonImageUpload(e.target.files)}
                />

                <Button
  variant="contained"
  startIcon={<CloudUploadIcon />}
  onClick={() => commonFileInputRef.current?.click()}
  disabled={isUploading}
  sx={{
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "none",
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    },
  }}
>
  {isUploading ? 'Uploading...' : 'Upload Image'}
</Button>

                {isUploading && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={uploadProgress}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          minWidth: 50,
                          textAlign: 'right',
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {uploadProgress}%
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Display uploaded common images */}
              {commonImagesState.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: theme.typography.subtitle_m?.fontWeight,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Uploaded Images ({commonImagesState.length})
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      onClick={handleClearAllCommonImages}
                      // ✅ FIX: Disable Clear All when all images are inherited and cannot be removed
                      disabled={allCommonImagesReadOnly}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Clear All
                    </Button>
                  </Box>
                  <Grid2 container spacing={2}>
                    {commonImagesState.map((image) => {
                      // ✅ FIX: Determine if this image is inherited & must be read-only
                      const imageIsReadOnly =
                        inheritedReadOnlyCommonImages && image?.isExisting;

                      return (
                        <Grid2 key={image.id} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                          <Card sx={{
                            position: 'relative',
                            border: `1px solid ${image.isDefault ? theme.palette.primary.main : theme.palette.greyscale?.stroke_1}`,
                            borderWidth: image.isDefault ? 2 : 1,
                            borderRadius: 1,
                            boxShadow: `0px 1px 2px rgba(0, 0, 0, 0.1)`,
                            '&:hover': {
                              boxShadow: `0px 4px 8px rgba(0, 0, 0, 0.15)`,
                              transform: 'translateY(-2px)',
                              transition: 'all 0.2s ease',
                            },
                          }}>
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                              <Box sx={{
                                position: 'relative',
                                width: '100%',
                                paddingBottom: '100%',
                                overflow: 'hidden',
                                borderRadius: '4px',
                                backgroundColor: theme.palette.background.default,
                              }}>
                                <Avatar
                                  src={image.filepathUrl || image.filepath || image.data}
                                  variant="square"
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                    (e.target as HTMLImageElement).src = sampleImageUrl;
                                  }}
                                />
                                {image.isDefault && (
                                  <Chip
                                    label="Default"
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: 4,
                                      right: 4,
                                      backgroundColor: theme.palette.primary.main,
                                      color: 'white',
                                      fontSize: '0.65rem',
                                      height: 20,
                                      zIndex: 1,
                                    }}
                                  />
                                )}
                                {imageIsReadOnly && (
                                  <Chip
                                    label="Locked"
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: 4,
                                      left: 4,
                                      backgroundColor: theme.palette.grey[700],
                                      color: 'white',
                                      fontSize: '0.65rem',
                                      height: 20,
                                      zIndex: 1,
                                    }}
                                  />
                                )}
                              </Box>

                              <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 1
                              }}>
                                <Tooltip title={image.name}>
                                  <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{
                                      flex: 1,
                                      color: theme.palette.text.secondary,
                                      fontSize: '0.7rem',
                                      maxWidth: '60%',
                                    }}
                                  >
                                    {image.name}
                                  </Typography>
                                </Tooltip>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSetDefaultCommonImage(image.id)}
                                    // ✅ FIX: Lock default flag for inherited common images
                                    disabled={imageIsReadOnly}
                                    sx={{ p: 0.5 }}
                                  >
                                    {image.isDefault ? (
                                      <StarIcon sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
                                    ) : (
                                      <StarBorderIcon sx={{ color: theme.palette.text.disabled, fontSize: 18 }} />
                                    )}
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveCommonImage(image.id)}
                                    // ✅ FIX: Lock delete for inherited common images
                                    disabled={imageIsReadOnly}
                                    sx={{
                                      p: 0.5,
                                      color: imageIsReadOnly ? theme.palette.text.disabled : theme.palette.error.main,
                                    }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid2>
                      );
                    })}
                  </Grid2>
                </Box>
              )}
            </Box>
          )}

          {/* Items Table */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: theme.typography.h6?.fontSize,
                  fontWeight: theme.typography.h6?.fontWeight
                }}
              >
                Item Variants List
              </Typography>

              {/* Search Input */}
              <TextField
                size="small"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Typography
              variant="body2"
              sx={{
                mb: 2,
                color: theme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              {imageType === 'itemwise'
                ? 'Use Add / Manage Images on each card to upload specific images for that variant. Variants without their own image will show the previously uploaded common image.'
                : 'Common images uploaded above will be applied to all variants below.'}
            </Typography>

            <Grid2 container spacing={2}>
              {filteredItems.map((item) => {
                const imageUrl = getItemImageUrl(item);
                const variantLines = getVariantLines(item);
                const currentDescription = updatedDescriptions[item.id] ?? item.description ?? "";
                const isEditing = editingDescriptionId === item.id;
                const sellingChecked = item.isSellingItem !== false;
                const inventoryChecked = item.isInventoryItem === true;
                const fieldLabelSx = {
                  display: "block",
                  mb: 0.5,
                  fontSize: theme.typography.body_xs?.fontSize || "0.75rem",
                  lineHeight: 1.3,
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                } as const;

                return (
                  <Grid2 key={item.id} size={{ xs: 12, md: 6 }}>
                    <Card
                      sx={{
                        height: "100%",
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.greyscale?.stroke_1 || theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: "none",
                      }}
                    >
                      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        <Box                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "56px 1fr auto",
                              sm: "56px minmax(0, 1fr) minmax(0, 1fr) auto",
                            },
                            columnGap: 1.5,
                            rowGap: 1,
                            alignItems: "flex-start",
                          }}
                        >
                          {/* 1. Image */}
                          <Avatar
                            src={imageUrl}
                            variant="rounded"
                            sx={{
                              width: 56,
                              height: 56,
                              gridRow: { xs: "1", sm: "1" },
                              flexShrink: 0,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.greyscale?.stroke_1 || theme.palette.divider}`,
                              backgroundColor: theme.palette.background.default,
                              "& img": { objectFit: "cover" },
                            }}
                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                              (e.target as HTMLImageElement).src = sampleImageUrl;
                            }}
                          >
                            <ImageIcon sx={{ color: theme.palette.greyscale?.icons, fontSize: 22 }} />
                          </Avatar>

                          {/* 2. Item Name */}
                          <Box sx={{ minWidth: 0, gridColumn: { xs: "2 / 3", sm: "2 / 3" } }}>
                            <Typography component="span" sx={fieldLabelSx}>
                              Item Name
                            </Typography>
                            {renderTruncatedText(item.itemname || "-", 26, {
                              fontWeight: 600,
                              fontSize: theme.typography.body2?.fontSize || "0.875rem",
                            })}
                          </Box>

                          {/* 3. Description (swapped with Variant) */}
                          <Box
                            sx={{
                              minWidth: 0,
                              gridColumn: { xs: "1 / -1", sm: "3 / 4" },
                            }}
                          >
                            {/* Updated Label with Edit Icon */}
                            <Typography component="span" sx={{ ...fieldLabelSx, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              Description
                              <EditIcon sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
                            </Typography>
                            
                            {isEditing ? (
                              <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                value={editingDescriptionValue}
                                onChange={(e) => setEditingDescriptionValue(e.target.value)}
                                onBlur={() => saveItemDescription(item.id, editingDescriptionValue)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    saveItemDescription(item.id, editingDescriptionValue);
                                  }
                                  if (e.key === "Escape") setEditingDescriptionId(null);
                                }}
                              />
                            ) : currentDescription ? (
                              <Tooltip title={currentDescription} arrow placement="top">
                                <Box
                                  onClick={() => {
                                    setEditingDescriptionId(item.id);
                                    setEditingDescriptionValue(currentDescription);
                                  }}
                                  sx={{ 
                                    cursor: "pointer", 
                                    minWidth: 0,
                                    p: 0.5,
                                    mx: -0.5,
                                    borderRadius: 0.5,
                                    transition: 'background-color 0.2s',
                                    '&:hover': {
                                      backgroundColor: theme.palette.action.hover,
                                    }
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.875rem",
                                      lineHeight: 1.4,
                                      color: theme.palette.text.primary,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {truncateDisplayText(currentDescription, 36).display}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            ) : (
                              <Box
                                onClick={() => {
                                  setEditingDescriptionId(item.id);
                                  setEditingDescriptionValue(currentDescription);
                                }}
                                sx={{ 
                                  cursor: "pointer", 
                                  minWidth: 0,
                                  p: 0.5,
                                  mx: -0.5,
                                  borderRadius: 0.5,
                                  transition: 'background-color 0.2s',
                                  '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                  }
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.875rem",
                                    fontStyle: "italic",
                                    color: "text.secondary",
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 14 }} /> Click to add description...
                                </Typography>
                              </Box>
                            )}
                          </Box>

                                                    {/* 4. Actions */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.25,
                              flexShrink: 0,
                              gridColumn: { xs: "3 / 4", sm: "4 / 5" },
                              gridRow: { xs: "1", sm: "1" },
                            }}
                          >
                            {imageType === "itemwise" && (
                              <Tooltip title="Add Images">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenUploadDialog(item)}
                                  sx={{ color: theme.palette.primary.main }}
                                >
                                  <AddCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {imageType === "itemwise" && (
                              <Tooltip title="Manage Images">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenManageImagesDialog(item)}
                                  sx={{ color: theme.palette.greyscale?.icons || theme.palette.text.secondary }}
                                >
                                  <ImageIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Remove">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveItemClick(item.id)}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {/* 5. Variants — full row, wrap chips */}
                          <Box
                            sx={{
                              minWidth: 0,
                              gridColumn: "1 / -1",
                              mt: 0.75,
                              pt: 1.5,
                              borderTop: `1px solid ${theme.palette.greyscale?.stroke_1 || theme.palette.divider}`,
                            }}
                          >
                            <Typography component="span" sx={fieldLabelSx}>
                              Variants
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                gap: 0.75,
                                alignItems: "center",
                              }}
                            >
                              {variantLines.map((line, index) => {
                                const fullLine =
                                  line.label === "Variant" ? line.value : `${line.label}: ${line.value}`;
                                const { display, full, truncated } = truncateDisplayText(fullLine, 20);
                                const chip = (
                                  <Chip
                                    key={`${item.id}-variant-${index}`}
                                    label={display}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      maxWidth: "100%",
                                      justifyContent: "flex-start",
                                      borderRadius: 1,
                                      border: `1px solid ${theme.palette.greyscale?.stroke_1 || theme.palette.divider}`,
                                      color: theme.palette.text.primary,
                                      backgroundColor: "transparent",
                                      fontSize: theme.typography.body_xs?.fontSize || "0.75rem",
                                      height: 26,
                                      "& .MuiChip-label": {
                                        px: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      },
                                    }}
                                  />
                                );
                                return truncated ? (
                                  <Tooltip key={`${item.id}-variant-tip-${index}`} title={full} arrow>
                                    <span>{chip}</span>
                                  </Tooltip>
                                ) : (
                                  chip
                                );
                              })}
                            </Box>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            mt: 1.75,
                            pt: 1.5,
                            borderTop: `1px solid ${theme.palette.greyscale?.stroke_1 || theme.palette.divider}`,
                          }}
                        >
                          <Typography component="span" sx={fieldLabelSx}>
                            Item Type
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={sellingChecked}
                                  onChange={() => handleItemTypeToggle(item, "selling")}
                                />
                              }
                              label="Selling Item"
                              sx={{
                                m: 0,
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "0.875rem",
                                  color: theme.palette.text.primary,
                                },
                              }}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={inventoryChecked}
                                  onChange={() => handleItemTypeToggle(item, "inventory")}
                                />
                              }
                              label="Inventory Item"
                              sx={{
                                m: 0,
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "0.875rem",
                                  color: theme.palette.text.primary,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid2>
                );
              })}
              {filteredItems.length === 0 && (
                <Grid2 size={{ xs: 12 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 4, textAlign: "center", fontSize: "0.875rem" }}
                  >
                    No variants found
                  </Typography>
                </Grid2>
              )}
            </Grid2>

            {/* ✅ Total Products/Variants to be created */}
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: `1px solid ${theme.palette.greyscale?.stroke_1 || theme.palette.divider}`,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                Total Products:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                {items.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 3,
          px: 3,
          pb: 3,
          borderTop: `1px solid ${theme.palette.greyscale?.stroke_1}`,
          pt: 3,
          backgroundColor: theme.palette.background.paper
        }}>
          <Button
            variant="outlined"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            onClick={handleUpdate}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={24} color="inherit" /> : (isEditMode ? "Modify" : "Submit")}
          </Button>
        </Box>

        {/* Remove Product Confirmation Dialog */}
        <AppDialog
          open={openRemoveDialog}
          onClose={cancelRemoveItem}
          titleId="remove-product-dialog-title"
          title="Confirm Remove Product"
          message={`Are you sure you want to remove the product "${itemToRemove?.itemname} - ${itemToRemove?.variant}"? This action cannot be undone.`}
          cancelLabel="Cancel"
          confirmLabel="Remove"
          confirmColor="error"
          onConfirm={confirmRemoveItem}
        />

        {/* Cancel/Back Confirmation Dialog */}
        <AppDialog
          open={openCancelDialog}
          onClose={cancelCancel}
          titleId="cancel-dialog-title"
          title={cancelActionType === "back" ? "Confirm Go Back" : "Confirm Cancel"}
          message={
            cancelActionType === "back"
              ? "Are you sure you want to go back? All unsaved changes will be lost."
              : "Are you sure you want to cancel? All unsaved changes will be lost."
          }
          cancelLabel={cancelActionType === "back" ? "No, Stay Here" : "No, Continue Editing"}
          confirmLabel={cancelActionType === "back" ? "Yes, Go Back" : "Yes, Cancel"}
          confirmColor="error"
          onConfirm={confirmCancel}
        />
      </Box>

      {/* Upload Dialog */}
      <AppDialog
        open={openUploadDialog}
        onClose={() => setOpenUploadDialog(false)}
        title={uploadMode === "common" ? "Upload Common Image" : "Upload Variant Image"}
        maxWidth="sm"
        fullWidth
        cancelLabel="Cancel"
        confirmLabel={isUploading ? "Uploading..." : "Upload"}
        confirmColor="primary"
        confirmDisabled={!selectedFile || isUploading}
        confirmLoading={isUploading}
        onConfirm={handleUploadConfirm}
      >
        <Box sx={{ mb: 3 }}>
          {/* Short description */}
          <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
            {uploadMode === "common"
              ? "Upload an image that will be applied to all item variants."
              : "Upload an image for this specific variant."}
          </Typography>

          {/* Item Name + Variant details — only for itemwise uploads */}
          {uploadMode === "itemwise" && selectedItemForUpload && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 1,
                border: `1px solid ${theme.palette.greyscale?.stroke_1 || theme.palette.divider}`,
                backgroundColor: theme.palette.background.default,
              }}
            >
              {/* Item Name row */}
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    mb: 0.25,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    textTransform: "none",
                    letterSpacing: "0.5px",
                  }}
                >
                  Item Name
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  {selectedItemForUpload.itemname || "-"}
                </Typography>
              </Box>

              {/* Variant row */}
              <Box>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    mb: 0.5,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    textTransform: "none",
                    letterSpacing: "0.5px",
                  }}
                >
                  Variant
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {getVariantLines(selectedItemForUpload).map((line, index) => {
                    const fullLine =
                      line.label === "Variant" ? line.value : `${line.label}: ${line.value}`;
                    return (
                      <Chip
                        key={`upload-variant-${index}`}
                        label={fullLine}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 1,
                          fontSize: "0.75rem",
                          height: 24,
                          border: `1px solid ${theme.palette.greyscale?.stroke_1 || theme.palette.divider}`,
                          color: theme.palette.text.primary,
                          backgroundColor: theme.palette.background.paper,
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <input
              type="file"
              ref={itemWiseFileInputRef}
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={handleFileInputChange}
            />

            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => itemWiseFileInputRef.current?.click()}
              fullWidth
              disabled={isUploading}
              sx={{ mb: 2, py: 2, textTransform: "none" }}
            >
              {isUploading
                ? "Uploading..."
                : fileName
                  ? `Selected: ${fileName}`
                  : "Click to select image file(s)"}
            </Button>

            {isUploading && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Box sx={{ width: "100%", mr: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={uploadProgress}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ minWidth: 50, textAlign: "right" }}>
                    {uploadProgress}%
                  </Typography>
                </Box>
              </Box>
            )}

            {/* --- MODIFIED SECTION: Reduced font size for error message --- */}
            {uploadError && (
              <Typography 
                variant="body2" 
                color="error" 
                sx={{ 
                  mb: 1, 
                  fontSize: "0.75rem", // Reduced font size
                  fontWeight: 500 
                }}
              >
                {uploadError}
              </Typography>
            )}
            {/* ------------------------------------------------------------------ */}
          </Box>
        </Box>
      </AppDialog>

      {/* Manage Itemwise Images Dialog */}
      <AppDialog
        open={openManageImagesDialog}
        onClose={() => setOpenManageImagesDialog(false)}
        title="Manage Variant Images"
        maxWidth="md"
        fullWidth
        cancelLabel="Close"
        onCancel={() => setOpenManageImagesDialog(false)}
      >
        {selectedItemForManage && (
          <>
            {/* Item Name + Variant info panel */}
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 1,
                border: `1px solid ${theme.palette.greyscale?.stroke_1 || theme.palette.divider}`,
                backgroundColor: theme.palette.background.default,
              }}
            >
              {/* Item Name row */}
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    mb: 0.25,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    textTransform: "none",
                    letterSpacing: "0.5px",
                  }}
                >
                  Item Name
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  {selectedItemForManage.itemname || "-"}
                </Typography>
              </Box>

              {/* Variant row */}
              <Box>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    mb: 0.5,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    textTransform: "none",
                    letterSpacing: "0.5px",
                  }}
                >
                  Variant
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {getVariantLines(selectedItemForManage).map((line, index) => {
                    const fullLine =
                      line.label === "Variant" ? line.value : `${line.label}: ${line.value}`;
                    return (
                      <Chip
                        key={`manage-variant-${index}`}
                        label={fullLine}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 1,
                          fontSize: "0.75rem",
                          height: 24,
                          border: `1px solid ${theme.palette.greyscale?.stroke_1 || theme.palette.divider}`,
                          color: theme.palette.text.primary,
                          backgroundColor: theme.palette.background.paper,
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {(() => {
  const ownImages = itemwiseImagesState[selectedItemForManage.id] || [];
  const commonImgs = commonImagesState || [];

  // Merge: own variant images first, then inherited common images.
  // De-dupe by id so the same common image is never shown twice.
  const seenIds = new Set<string | number>();
  const displayImages: ImageData[] = [];
  [...ownImages, ...commonImgs].forEach((img) => {
    const key = String(img.id);
    if (seenIds.has(key)) return;
    seenIds.add(key);
    displayImages.push(img);
  });

  if (displayImages.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body2" color="textSecondary">
          No images uploaded for this variant.
        </Typography>
      </Box>
    );
  }

  // "isInherited" now means: the currently rendered image is an inherited
  // common image, not a variant-specific one. We check per-image below,
  // so the info banner is shown only when there are no own images yet.
  const isInherited = ownImages.length === 0;

  return (
    <>
      
            <Grid2 container spacing={1.5}>
        {displayImages.map((image) => {
          const imageIsInherited = inheritedReadOnlyCommonImages && image?.isExisting;

          return (
            <Grid2 key={image.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ position: "relative", maxWidth: 180, mx: "auto" }}>
                <CardContent sx={{ p: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mb: 1,
                      position: "relative",
                      width: "100%",
                      paddingBottom: "100%",
                      backgroundColor: theme.palette.background.default,
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Avatar
                      src={image.filepathUrl || image.filepath || image.data}
                      variant="square"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        background: "transparent",
                        "& img": {
                          objectFit: "cover",
                        },
                      }}
                    />
                    {image.isDefault && (
                      <Chip
                        label="Default"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: theme.palette.primary.main,
                          color: "white",
                          fontSize: "0.65rem",
                          height: 20,
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                      {image.name}
                    </Typography>
                    <IconButton
                      size="small"
                      disabled={imageIsInherited}
                      onClick={() =>
                        handleSetDefaultItemwiseImage(
                          selectedItemForManage.id,
                          image.id
                        )
                      }
                    >
                      {image.isDefault ? (
                        <StarIcon
                          sx={{ color: theme.palette.warning.main, fontSize: 20 }}
                        />
                      ) : (
                        <StarBorderIcon
                          sx={{ color: theme.palette.text.disabled, fontSize: 20 }}
                        />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={
                        imageIsInherited || (isEditMode && image?.isExisting)
                      }
                      onClick={() =>
                        handleRemoveItemwiseImage(
                          selectedItemForManage.id,
                          image.id
                        )
                      }
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid2>
          );
        })}
      </Grid2>
    </>
  );
})()}
          </>
        )}
      </AppDialog>
    </>
  );
}

export default ItemListView;